import { motion } from 'framer-motion';

const tireSizes = [
  'R14', 'R15', 'R16', 'R17', 'R18', 'R19', 'R20', 'R21', 'R22', 'R23'
];

export const TireInventory = ({ settings }) => {
  const usedTirePrice = settings?.used_tire_price || 50;

  return (
    <section id="tires" className="py-16 lg:py-24 bg-[#0A0A0A] relative" data-testid="tires-section">
      <div className="absolute inset-0 grid-bg opacity-20" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 lg:mb-16"
        >
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-6xl text-white mb-4" data-testid="tires-title">
            Our Tires
          </h2>
          <p className="font-body text-white/60 text-base lg:text-lg max-w-2xl mx-auto">
            New and quality used tires for all vehicle types
          </p>
          <div className="w-16 lg:w-24 h-1 bg-[#FF2E2E] mx-auto mt-4 lg:mt-6" />
        </motion.div>

        {/* Two columns: New Tires and Used Tires */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-10 lg:mb-12">
          {/* New Tires Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass border border-[#FF2E2E]/30 p-6 lg:p-8 text-center"
            data-testid="new-tires-card"
          >
            <div className="mb-4">
              <svg viewBox="0 0 80 80" className="w-24 h-24 lg:w-32 lg:h-32 mx-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Outer tire */}
                <circle cx="40" cy="40" r="36" stroke="#FF2E2E" strokeWidth="3"/>
                {/* Tread pattern */}
                <circle cx="40" cy="40" r="30" stroke="#FF2E2E" strokeWidth="6" strokeDasharray="8 4"/>
                {/* Inner rim */}
                <circle cx="40" cy="40" r="20" stroke="#FF2E2E" strokeWidth="2"/>
                {/* Hub */}
                <circle cx="40" cy="40" r="12" stroke="#FF2E2E" strokeWidth="2"/>
                <circle cx="40" cy="40" r="6" fill="#FF2E2E"/>
                {/* Lug nuts */}
                <circle cx="40" cy="28" r="2.5" fill="#FF2E2E"/>
                <circle cx="51" cy="34" r="2.5" fill="#FF2E2E"/>
                <circle cx="51" cy="46" r="2.5" fill="#FF2E2E"/>
                <circle cx="40" cy="52" r="2.5" fill="#FF2E2E"/>
                <circle cx="29" cy="46" r="2.5" fill="#FF2E2E"/>
                <circle cx="29" cy="34" r="2.5" fill="#FF2E2E"/>
              </svg>
            </div>
            <h3 className="font-heading text-2xl lg:text-3xl text-white mb-2">New Tires</h3>
            <p className="font-body text-white/60 text-sm mb-4">All popular sizes in stock</p>
            <p className="font-body text-white/70 mb-4">
              Wide selection of quality new tires for all vehicle types. Competitive prices and professional installation.
            </p>
            <div className="glass border border-[#FF2E2E]/50 p-3 inline-block">
              <p className="font-heading text-[#FF2E2E] text-sm lg:text-base tracking-wider">
                CALL FOR BEST PRICE
              </p>
            </div>
          </motion.div>

          {/* Used Tires Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass border border-[#FF2E2E]/30 p-6 lg:p-8 text-center"
            data-testid="used-tires-card"
          >
            <div className="mb-4">
              <p className="font-body text-white/60 text-lg mb-1">from</p>
              <span className="font-heading text-7xl lg:text-9xl text-[#FF2E2E] neon-text">${usedTirePrice}</span>
            </div>
            <h3 className="font-heading text-2xl lg:text-3xl text-white mb-2">Used Tires</h3>
            <p className="font-body text-white/60 text-sm mb-4">Quality inspected • Ready to mount</p>
            <div className="glass border border-[#FF2E2E]/50 p-3 inline-block">
              <p className="font-heading text-[#FF2E2E] text-sm lg:text-base tracking-wider">
                FREE INSTALLATION & BALANCING
              </p>
            </div>
          </motion.div>
        </div>

        {/* Available Sizes Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass border border-[#FF2E2E]/30 p-6 lg:p-10 text-center"
          data-testid="sizes-banner"
        >
          <h3 className="font-heading text-xl lg:text-2xl text-white mb-2">Available Sizes</h3>
          <p className="font-body text-white/60 text-sm lg:text-base mb-6">
            We stock new and used tires in all popular sizes
          </p>
          
          {/* Sizes Grid */}
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 lg:gap-3" data-testid="sizes-grid">
            {tireSizes.map((size, index) => (
              <motion.div
                key={size}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#121212] border border-[#FF2E2E]/40 hover:border-[#FF2E2E] hover:bg-[#FF2E2E]/10 p-3 lg:p-4 transition-all duration-300 cursor-default"
                data-testid={`size-${size}`}
              >
                <span className="font-heading text-lg lg:text-2xl text-[#FF2E2E]">{size}</span>
              </motion.div>
            ))}
          </div>

          <p className="font-body text-white/40 text-xs lg:text-sm mt-6">
            Don't see your size? Contact us — we can order any size for you!
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default TireInventory;
